import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Avatar, Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useAuth } from '../stores/auth-store';
import { useLanguage } from '../stores/language-store';
import { useSidebar } from './sidebar/sidebar-provider';
import LanguageSwitcher from './language-switcher';
import ThemeSwitcher from './theme-switcher';
import CommandPalette from './command-palette';
import { ProfileModal } from './profile-modal';

const Header: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { user, logout } = useAuth();
  const { isRTL } = useLanguage();
  const { isMobile, toggleSidebar, toggleMiniSidebar } = useSidebar();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  
  // Keyboard shortcut listener
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // "/" key (like GitHub, Notion)
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        // Only if no input is focused
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          e.preventDefault();
          e.stopPropagation();
          setIsCommandPaletteOpen(true);
          return false;
        }
      }
    };
    
    // Use capture phase to prevent browser default before React events
    document.addEventListener('keydown', down, { capture: true });
    return () => document.removeEventListener('keydown', down, { capture: true });
  }, []);
  
  return (
    <>
    <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
      <div className="flex items-center justify-between px-4 h-16">
        <div className="flex items-center gap-3">
          <Button 
            isIconOnly 
            variant="light" 
            onPress={isMobile ? toggleSidebar : toggleMiniSidebar}
          >
            <Icon icon="lucide:menu" width={24} />
          </Button>
          
          <div className="hidden md:block">
            <div
              onClick={() => setIsCommandPaletteOpen(true)}
              className="flex items-center gap-3 px-4 h-10 min-w-[200px] lg:min-w-[300px] rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-gray-50 dark:bg-gray-800/50 cursor-pointer transition-colors"
            >
              <Icon icon="lucide:search" className="text-gray-400" width={18} />
              <span className="text-sm text-gray-500 dark:text-gray-400 flex-1">
                {t('search')}
              </span>
              <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-xs font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm">
                /
              </kbd>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          
          <ThemeSwitcher />
          
          <LanguageSwitcher />
          
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Avatar 
                src={user?.avatar || undefined}
                icon={<Icon icon="lucide:user" width={18} className="text-gray-500" />}
                isBordered
                size="sm"
                color="default"
                className="cursor-pointer"
              />
            </DropdownTrigger>
            <DropdownMenu 
              aria-label="User menu"
              onAction={(key) => {
                const action = String(key);
                if (action === 'profile') {
                  setIsProfileModalOpen(true);
                } else if (action === 'settings') {
                  history.push('/settings');
                } else if (action === 'logout') {
                  logout();
                }
              }}
            >
              <DropdownItem
                key="profile"
                startContent={<Icon icon="lucide:user" width={18} />}
              >
                {t('profile')}
              </DropdownItem>
              <DropdownItem
                key="settings"
                startContent={<Icon icon="lucide:settings" width={18} />}
              >
                {t('settings')}
              </DropdownItem>
              <DropdownItem
                key="help"
                startContent={<Icon icon="lucide:help-circle" width={18} />}
              >
                {t('help')}
              </DropdownItem>
              <DropdownItem
                key="logout"
                startContent={<Icon icon="lucide:log-out" width={18} />}
                color="danger"
                showDivider
              >
                {t('logout')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      
      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
      
      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />
    </header>
    </>
  );
};

export default Header;
